import { ActionHandlerParams } from '../types';

import {
  addToCurrentStep,
  playAudioFromStep,
  validateForm,
  checkAllFieldsHaveValues,
  getFieldsCurrentStep,
  skipValidationForm,
  scrollToErrorField,
} from '../helpers/config-helper';
import { isFunction, redirectTo } from '@r-and-a-shared-ui/utils';
import {
  ppUpdateHandler,
  ppCreateOnlineEntryHandler,
  ppCreateAccreditationContactHandler,
} from './pp-form-action-handlers';
import { getStepIndexKey } from '../helpers/form-steps-helper';

export type PPFormCtaActionTypes =
  | 'redirect_to'
  | 'redirect_to_preserve_query'
  | 'next_step'
  | 'skip_step'
  | 'previous_step'
  | 'update'
  | 'update_next_step'
  | 'update_exit'
  | 'reset_exit'
  | 'skip_with_no_validation'
  | 'undefined'
  | 'create_accreditation_contact'
  | 'create_online_entry'
  | 'create_online_entry_next'
  | 'create_online_entry_redirect_to_preserve_query';

export interface PPCtaConfig {
  trpcRoute?: string;
  actionHandler: (params: ActionHandlerParams) => any;
  clientSideMutation?: (...args: any[]) => any;
}

export const ppFormCtaConfig: Record<PPFormCtaActionTypes, PPCtaConfig> = {
  redirect_to: {
    actionHandler: params => {
      const { resource: url } = params;
      redirectTo(url);
    },
  },
  redirect_to_preserve_query: {
    actionHandler: params => {
      const { resource: url } = params;
      redirectTo(url, true);
    },
  },
  next_step: {
    actionHandler: async params => {
      const passedCheck = await validateForm(params);
      if (!passedCheck) {
        scrollToErrorField();
        return;
      }
      const steps = params.formContentProps?.steps;
      const stepIndexKey = getStepIndexKey(
        params.formContentProps?.formCodename,
      );
      const fields = getFieldsCurrentStep(steps, stepIndexKey);
      const allFieldsHaveValues = checkAllFieldsHaveValues(
        fields,
        params.submitData,
      );
      const skippingStepConfig = {
        values: params.submitData,
        steps: params.formContentProps?.steps,
        setValue: params.setValue,
      };
      addToCurrentStep(1, stepIndexKey, skippingStepConfig);

      if (allFieldsHaveValues) {
        playAudioFromStep(steps, stepIndexKey);
      }
    },
  },
  skip_step: {
    actionHandler: async params => {
      const passedCheck = await validateForm(params);

      if (!passedCheck) {
        return;
      }
      const skippingStepConfig = {
        values: params.submitData,
        steps: params.formContentProps?.steps,
        setValue: params.setValue,
      };
      const stepIndexKey = getStepIndexKey(
        params.formContentProps?.formCodename,
      );
      addToCurrentStep(1, stepIndexKey, skippingStepConfig);
    },
  },
  skip_with_no_validation: {
    actionHandler: params => {
      skipValidationForm(params);

      const skippingStepConfig = {
        values: params.submitData,
        steps: params.formContentProps?.steps,
        setValue: params.setValue,
      };
      const stepIndexKey = getStepIndexKey(
        params.formContentProps?.formCodename,
      );
      addToCurrentStep(1, stepIndexKey, skippingStepConfig);
    },
  },
  previous_step: {
    actionHandler: params => {
      const skippingStepConfig = {
        values: params.submitData,
        steps: params.formContentProps?.steps,
        setValue: params.setValue,
      };
      const stepIndexKey = getStepIndexKey(
        params.formContentProps?.formCodename,
      );
      addToCurrentStep(-1, stepIndexKey, skippingStepConfig);
    },
  },
  update: {
    trpcRoute: 'update',
    actionHandler: ppUpdateHandler,
  },
  update_next_step: {
    trpcRoute: 'update',
    actionHandler: async params => {
      const result = await ppFormCtaConfig['update'].actionHandler(params);
      if (result.hasErrors) {
        return;
      }
      ppFormCtaConfig['next_step'].actionHandler(params);
    },
  },
  update_exit: {
    trpcRoute: 'update',
    actionHandler: async params => {
      const result = await ppFormCtaConfig['update'].actionHandler(params);
      if (result.hasErrors) {
        return;
      }
      params.customClientCTAHandler?.(false);
    },
  },
  reset_exit: {
    actionHandler: params => {
      const { customClientCTAHandler } = params;
      if (isFunction(customClientCTAHandler)) {
        customClientCTAHandler();
      }
    },
  },
  undefined: {
    actionHandler: () => undefined,
  },

  create_accreditation_contact: {
    trpcRoute: 'create_accreditation_contact',
    actionHandler: async params => {
      const result = await ppCreateAccreditationContactHandler(params);
      if (result?.hasErrors) {
        return;
      }

      params.customClientCTAHandler?.(false);
    },
  },
  create_online_entry: {
    trpcRoute: 'create_online_entry',
    actionHandler: ppCreateOnlineEntryHandler,
  },
  create_online_entry_next: {
    trpcRoute: 'create_online_entry',
    actionHandler: async params => {
      const result = await ppFormCtaConfig['create_online_entry'].actionHandler(
        params,
      );
      if (result.hasErrors) {
        return;
      }
      ppFormCtaConfig['next_step'].actionHandler(params);
    },
  },
  create_online_entry_redirect_to_preserve_query: {
    trpcRoute: 'create_online_entry',
    actionHandler: async params => {
      const result = await ppFormCtaConfig['create_online_entry'].actionHandler(
        params,
      );
      if (result.hasErrors || !result?.data?.redirectURL) {
        return;
      }
      redirectTo(result.data.redirectURL, true);
    },
  },
};

export default ppFormCtaConfig;
